/**
 * AI 議題工作區 — 議題清單頁
 * func_code: ee_research_topics（路由 /ee_research_topics）
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Stack, Button, TextField, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  CircularProgress, Typography, Divider,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon, Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon, TrackChanges as TargetIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import MindMapDecomposeDialog from '../components/research/MindMapDecomposeDialog';
import { useMessage } from '../contexts/MessageContext';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import { Topic, TopicCreate, TopicUpdate, Subject } from '../types/research';
import {
  listTopics, createTopic, updateTopic, deleteTopic, listSubjects,
} from '../services/researchService';
import { formatLocal } from '../utils/dateHelper';

const FUNC_CODE = 'ee_research_topics';

const fmt = (iso?: string | null) => iso ? formatLocal(iso) : '—';

interface TopicFormData {
  topic_code: string;
  topic_name: string;
  description: string;
  subject_ids: number[];              // M:N 歸屬（空陣列 = 無歸屬）
  primary_subject_id: number | null;  // 主場主題（必須在 subject_ids 內）
  tag_dimensions_text: string;        // 逗號分隔
  search_keywords_text: string;       // 逗號分隔
  daily_item_quota: number;
  auto_collect_enabled: boolean;
  relevance_threshold: number;
  is_active: boolean;
}

const emptyForm: TopicFormData = {
  topic_code: '',
  topic_name: '',
  description: '',
  subject_ids: [],
  primary_subject_id: null,
  tag_dimensions_text: '地區, 面向, 事件日期',
  search_keywords_text: '',
  daily_item_quota: 5,
  auto_collect_enabled: false,
  relevance_threshold: 0.6,
  is_active: true,
};

const splitCSV = (s: string): string[] =>
  s.split(/[,，]/).map(x => x.trim()).filter(Boolean);

const ResearchTopicsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [topics, setTopics] = useState<Topic[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // 主題篩選：由 URL ?subject_id=X 控制（X=0 表示「無歸屬」）；空字串 = 全部
  const subjectFilter = searchParams.get('subject_id') ?? '';

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Topic | null>(null);
  const [form, setForm] = useState<TopicFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [mindMapOpen, setMindMapOpen] = useState(false);

  // 載入主題清單（給 filter 跟編輯表單下拉用）
  useEffect(() => {
    (async () => {
      try {
        const list = await listSubjects({ is_active: true });
        setSubjects(list);
      } catch { /* 沒主題沒關係，不擋議題列表 */ } // silent ok: optional subjects for filter, non-blocking
    })();
  }, []);

  const setSubjectFilter = (val: string) => {
    const sp = new URLSearchParams(searchParams);
    if (val === '') sp.delete('subject_id');
    else sp.set('subject_id', val);
    setSearchParams(sp, { replace: true });
  };

  // 採納 AI 議題拆解結果：merge 進現有 form（不覆蓋 user 已輸入的，採追加邏輯）
  const handleAdoptDecomposition = (patch: {
    description?: string;
    keywords?: string[];
    tag_dimensions?: string[];
  }) => {
    setForm((prev) => {
      const next = { ...prev };
      if (patch.description && !prev.description.trim()) {
        next.description = patch.description;
      }
      if (patch.keywords?.length) {
        const existing = prev.search_keywords_text
          ? prev.search_keywords_text.split(/[,，]/).map(s => s.trim()).filter(Boolean)
          : [];
        const merged = Array.from(new Set([...existing, ...patch.keywords]));
        next.search_keywords_text = merged.join(', ');
      }
      if (patch.tag_dimensions?.length) {
        const existing = prev.tag_dimensions_text
          ? prev.tag_dimensions_text.split(/[,，]/).map(s => s.trim()).filter(Boolean)
          : [];
        const merged = Array.from(new Set([...existing, ...patch.tag_dimensions]));
        next.tag_dimensions_text = merged.join(', ');
      }
      return next;
    });
    showSuccess('SYS020002', { name: 'AI 拆解採納', id: '' });
  };

  const reload = useCallback(async () => {
    setLoading(true);
    const params: any = { search: search || undefined };
    if (subjectFilter !== '') params.subject_id = Number(subjectFilter);
    try {
      const data = await listTopics(params);
      setTopics(data);
      logView(FUNC_CODE, { ...params, count: data.length });
    } catch (err: any) {
      logView(FUNC_CODE, params, String(err?.message || err));
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [search, subjectFilter, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    // 若目前 filter 鎖定某主題，新建時直接帶該主題為主場（讓「在主題下新增議題」流暢）
    const presetId = subjectFilter && subjectFilter !== '0'
      ? Number(subjectFilter) : null;
    setForm({
      ...emptyForm,
      subject_ids: presetId ? [presetId] : [],
      primary_subject_id: presetId,
    });
    setEditOpen(true);
  };

  const openEdit = (t: Topic) => {
    setEditing(t);
    const subjects = t.subjects || [];
    const primary = subjects.find(s => s.is_primary);
    setForm({
      topic_code: t.topic_code,
      topic_name: t.topic_name,
      description: t.description || '',
      subject_ids: subjects.map(s => s.id),
      primary_subject_id: primary?.id ?? null,
      tag_dimensions_text: (t.tag_dimensions || []).join(', '),
      search_keywords_text: (t.search_keywords || []).join(', '),
      daily_item_quota: t.daily_item_quota,
      auto_collect_enabled: t.auto_collect_enabled,
      relevance_threshold: t.relevance_threshold,
      is_active: t.is_active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload: any = {
      topic_code: form.topic_code.trim(),
      topic_name: form.topic_name.trim(),
      description: form.description || undefined,
      tag_dimensions: splitCSV(form.tag_dimensions_text),
      search_keywords: splitCSV(form.search_keywords_text),
      daily_item_quota: form.daily_item_quota,
      auto_collect_enabled: form.auto_collect_enabled,
      relevance_threshold: form.relevance_threshold,
      is_active: form.is_active,
    };
    // Subject-led（v0.3）：歸屬主題只在 CREATE 時 auto-attach（從 filter 進來那條路）；
    // EDIT 時完全不帶 — 由主題詳細頁的「管理旗下議題」管理。
    if (!editing && form.subject_ids.length > 0) {
      payload.subject_ids = form.subject_ids;
      payload.primary_subject_id = form.primary_subject_id;
    }
    try {
      if (editing) {
        const { topic_code, ...updateData } = payload;
        await updateTopic(editing.id, updateData as TopicUpdate);
        logUpdate(FUNC_CODE,
          { ...editing },
          { ...payload, id: editing.id });
        showSuccess('SYS020002', { name: form.topic_name, id: String(editing.id) });
      } else {
        const created = await createTopic(payload as TopicCreate);
        logCreate(FUNC_CODE, { ...created });
        showSuccess('SYS020001', { name: form.topic_name });
      }
      setEditOpen(false);
      reload();
    } catch (err: any) {
      if (editing) {
        logUpdate(FUNC_CODE, { ...editing }, payload, String(err?.message || err));
      } else {
        logCreate(FUNC_CODE, payload, String(err?.message || err));
      }
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: Topic) => {
    if (!window.confirm(`確定刪除議題「${t.topic_name}」？\n（其下所有來源、素材、報告也會一併刪除）`)) {
      return;
    }
    try {
      await deleteTopic(t.id);
      logDelete(FUNC_CODE, { ...t });
      showSuccess('SYS020003', { name: t.topic_name, id: String(t.id) });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE, { ...t }, String(err?.message || err));
      showApiError(err);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <FunctionPageHeader funcCode="ee_research_topics" fallback="議題管理" />
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            新增議題
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small" placeholder="搜尋議題代碼或名稱"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>歸屬主題</InputLabel>
          <Select
            label="歸屬主題"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(String(e.target.value))}
          >
            <MenuItem value="">全部議題</MenuItem>
            <MenuItem value="0">
              <em>無歸屬主題</em>
            </MenuItem>
            <Divider />
            {subjects.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                <TargetIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                {s.subject_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {subjectFilter !== '' && (
          <Tooltip title="清除主題篩選">
            <IconButton size="small" onClick={() => setSubjectFilter('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >重新整理</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell width={120}>代碼</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell width={180}>歸屬主題</TableCell>
              <TableCell>標籤維度</TableCell>
              <TableCell width={90} align="right">每日上限</TableCell>
              <TableCell width={100}>自動蒐集</TableCell>
              <TableCell width={170}>上次蒐集</TableCell>
              <TableCell width={70}>啟用</TableCell>
              <TableCell width={140} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topics.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : '尚無議題'}
                </TableCell>
              </TableRow>
            ) : (
              topics.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {t.topic_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.topic_name}</Typography>
                    {t.description && (
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: 'block' }}>
                        {t.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(t.subjects && t.subjects.length > 0) ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(() => {
                          const primary = t.subjects.find(s => s.is_primary) || t.subjects[0];
                          const others = t.subjects.filter(s => s.id !== primary.id);
                          return (
                            <>
                              <Tooltip title={primary.is_primary
                                ? `主場：${primary.subject_name}（點擊只看此主題下的議題）`
                                : `${primary.subject_name}（點擊只看此主題下的議題）`}>
                                <Chip
                                  size="small" icon={<TargetIcon />}
                                  label={primary.subject_name}
                                  clickable
                                  onClick={() => setSubjectFilter(String(primary.id))}
                                  color={primary.is_primary ? 'primary' : 'default'}
                                  variant={primary.is_primary ? 'filled' : 'outlined'}
                                />
                              </Tooltip>
                              {others.length > 0 && (
                                <Tooltip title={`另掛 ${others.length} 個主題：${others.map(o => o.subject_name).join(' / ')}`}>
                                  <Chip
                                    size="small"
                                    label={`+${others.length}`}
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </>
                          );
                        })()}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(t.tag_dimensions || []).map(dim => (
                        <Chip key={dim} label={dim} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{t.daily_item_quota}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.auto_collect_enabled ? '開' : '關'}
                      size="small"
                      color={t.auto_collect_enabled ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(t.last_auto_collect_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.is_active ? '是' : '否'}
                      size="small"
                      color={t.is_active ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="進入工作區">
                      <IconButton size="small" color="primary"
                                  onClick={() => navigate(`/ee_research_topics/${t.topic_code}`)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canUpdate && (
                      <Tooltip title="編輯">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="刪除">
                        <IconButton size="small" color="error" onClick={() => handleDelete(t)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增 / 編輯議題 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '編輯議題' : '新增議題'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="議題代碼" required size="small"
              value={form.topic_code}
              onChange={e => setForm({ ...form, topic_code: e.target.value })}
              disabled={!!editing}
              helperText="英數底線；建立後不可改"
            />
            <TextField
              label="議題名稱" required size="small"
              value={form.topic_name}
              onChange={e => setForm({ ...form, topic_name: e.target.value })}
            />
            {/* 歸屬主題：v0.3 改 readonly — 主題位階高於議題，從主題詳細頁管理 */}
            <Box>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}>
                被以下主題引用（在主題詳細頁管理 → 旗下議題）
              </Typography>
              {editing && editing.subjects && editing.subjects.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {editing.subjects.map(s => (
                    <Chip key={s.id} size="small"
                      icon={s.is_primary ? <span>🏠</span> : <TargetIcon />}
                      label={s.subject_name}
                      color={s.is_primary ? 'primary' : 'default'}
                      variant={s.is_primary ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  尚未被任何主題引用
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!form.topic_name.trim()
                ? '請先填議題名稱' : 'AI 自動拆解這個議題的方向、關鍵字、維度'}>
                <span>
                  <Button
                    size="small" variant="outlined" startIcon={<AutoAwesomeIcon />}
                    disabled={!form.topic_name.trim()}
                    onClick={() => setMindMapOpen(true)}
                  >
                    ✨ AI 拆解議題
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <TextField
              label="簡述" multiline rows={2} size="small"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
            />
            <TextField
              label="標籤維度" size="small"
              value={form.tag_dimensions_text}
              onChange={e => setForm({ ...form, tag_dimensions_text: e.target.value })}
              helperText="逗號分隔，例如：地區, 面向, 事件日期"
            />
            <TextField
              label="搜尋關鍵字（給 AI 評分相關性用）" size="small"
              value={form.search_keywords_text}
              onChange={e => setForm({ ...form, search_keywords_text: e.target.value })}
              helperText="逗號分隔；例：產業趨勢, 法規動態, 政策方向"
            />
            <Divider />
            <Stack direction="row" spacing={2}>
              <TextField
                label="每日蒐集上限" type="number" size="small" sx={{ flex: 1 }}
                value={form.daily_item_quota}
                onChange={e => setForm({ ...form, daily_item_quota: Number(e.target.value) })}
                inputProps={{ min: 1, max: 100 }}
              />
              <TextField
                label="相關性閾值" type="number" size="small" sx={{ flex: 1 }}
                value={form.relevance_threshold}
                onChange={e => setForm({ ...form, relevance_threshold: Number(e.target.value) })}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                helperText="0–1，低於此值丟棄"
              />
            </Stack>
            <Stack direction="row" spacing={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.auto_collect_enabled}
                    onChange={e => setForm({ ...form, auto_collect_enabled: e.target.checked })}
                  />
                }
                label="自動蒐集"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  />
                }
                label="啟用"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button onClick={handleSave} variant="contained"
                  disabled={saving || !form.topic_code || !form.topic_name}>
            {saving ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI 議題拆解 mind map */}
      <MindMapDecomposeDialog
        open={mindMapOpen}
        onClose={() => setMindMapOpen(false)}
        topicName={form.topic_name}
        description={form.description}
        onAdopt={handleAdoptDecomposition}
      />
    </Box>
  );
};

export default ResearchTopicsPage;